import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { RuleAction } from '@kkp-fraud/shared';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, COOKIE_MODE, api } from '../api';
import {
  CASE_FILTER_MAX,
  CASE_LABELS,
  CASE_PRIORITIES,
  CASE_STATUSES,
  CASE_TEXT_FILTERS,
  STATUS_CLASS,
  bangkokDayRange,
  caseLink,
  noteEditable,
  overlongFilters,
  precisionText,
  sortPrecision,
  type Case,
  type CaseFilters,
  type CaseLabel,
  type CaseNote,
  type CasePriority,
  type CaseStatus,
  type CaseTextFilter,
  type InvestigateDecision,
  type PrecisionSortKey,
  type RulePrecisionReport,
} from '../cases';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { ActionChip, DecisionChip } from '../components/Chips';
import { PageHead, SubTabs, Tip } from '../components/PageHead';
import { DEMO, isDemoCase } from '../demo';
import { actorLabel, dateTime, ms, num, thaiDay } from '../format';
import { When } from '../components/When';
import { Kv } from '../components/Kv';
// Wave 7 long windows
import { HitDetail } from '../components/LongWindowNotes';
import { CardLink, RuleLinks } from '../components/Links';
import { usePrimarySetId } from '../hooks/usePrimarySetId';
import { t } from '../i18n';
import { NOT_A_RULE, ruleLink } from '../links';
import { canEdit } from '../session';

/**
 * Investigate (R9) — cases tied to one decision each, the rules that fired on it, and a status
 * workflow; the analysts' labels become per-rule precision.
 *
 * `/investigate` has three tabs: the case list (filters + status chips), a card's decision
 * timeline (search by card id — the only way decisions can be searched; merchant, customer,
 * rule and assignee are searched on cases), and rule precision. `/investigate/:cardId/:caseId`
 * is one case. The admin service enforces the workflow and sends each case's
 * `allowedTransitions`, which are the only buttons shown. A case is never deleted.
 */
export function InvestigateScreen() {
  const { cardId, caseId } = useParams();
  return cardId && caseId ? <CaseDetail key={`${cardId}/${caseId}`} cardId={cardId} id={caseId} /> : <InvestigateHome />;
}

/** Writes need the admin service (cookie mode) and a writing role; a demo case is never written. */
function useMayWrite(): (id?: string) => boolean {
  const { session } = useShell();
  return (id?: string) => COOKIE_MODE && canEdit(session) && !(id !== undefined && isDemoCase(id));
}

function errorText(e: unknown): string {
  if (e instanceof ApiError && e.problems?.length) return e.problems.map((p) => `${p.field}: ${p.message}`).join('; ');
  return (e as Error).message;
}

const statusLabel = (s: CaseStatus) => t(`cases.status.${s}.label`);

/**
 * The status, and beside it the outcome whenever the case carries one its status does not
 * already say (QA-I-04). The BFF keeps two things: `status`, where the case is in the workflow
 * now — what the status chips filter — and `resolution`, the last outcome it was given — what the
 * Outcome filter finds. The outcome stays on the case when it is closed (shown as the outcome)
 * and when it is sent back for review to change it (shown as the previous outcome: it no longer
 * counts in rule precision until a new one is chosen). So every row the Outcome filter lists
 * shows that outcome.
 */
function StatusChip({ status, resolution }: { status: CaseStatus; resolution?: CaseLabel }) {
  const outcome = resolution && resolution !== status ? resolution : undefined;
  return (
    <span className="row-wrap" style={{ gap: 4 }}>
      <span className={`chip ${STATUS_CLASS[status]}`} data-testid="case-status">
        {status === 'Closed' && !resolution ? t('cases.closed.no_outcome.label') : statusLabel(status)}
      </span>
      {outcome &&
        (status === 'Closed' ? (
          <span className={`chip ${STATUS_CLASS[outcome]}`} data-testid="case-outcome" title={t('cases.filter.resolution')}>
            {statusLabel(outcome)}
          </span>
        ) : (
          // Not a chip: it is not where the case is now. Plain text also wraps in a narrow column.
          <span className="small muted case-outcome" data-testid="case-outcome">
            {t('cases.outcome.previous', { outcome: statusLabel(outcome) })}
          </span>
        ))}
    </span>
  );
}

/** Where a case page was opened from — its "← back" returns there (QA-I-07). */
type FromState = { from?: string } | null;
function useFrom(): { from: string } {
  const { pathname, search } = useLocation();
  return { from: pathname + search };
}

/** The rules that fired — each a link to the rule in Rule Management (UX review P1-3); `plain` inside a dialog. */
// 2c rule sets: a rule of a decision that evaluated several sets carries its set — `cnp-risk/RB-01`.
function RuleIds({ rules, plain }: { rules: { ruleId: string; ruleSetId?: string }[]; plain?: boolean }) {
  if (rules.length === 0) return <span className="muted">—</span>;
  const ids = rules.map((r) => (r.ruleSetId ? `${r.ruleSetId}/${r.ruleId}` : r.ruleId));
  return plain ? <span className="mono small">{ids.join(', ')}</span> : <RuleLinks ids={ids} />;
}

// ---- home: three tabs ------------------------------------------------------------------------

const TABS = ['cases', 'decisions', 'precision'] as const;
type Tab = (typeof TABS)[number];

function InvestigateHome() {
  const [params, setParams] = useSearchParams();
  const card = params.get('card') ?? '';
  // An unknown `?tab=` (an old or mistyped link) falls back to the default tab (QA-I-06).
  const asked = params.get('tab');
  const tab: Tab = TABS.includes(asked as Tab) ? (asked as Tab) : card ? 'decisions' : 'cases';
  const select = (next: string) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHead title={t('cases.title')} lead={t('cases.lead')} />
      {!COOKIE_MODE && !DEMO && (
        <div className="banner warn" role="status" data-testid="cases-unavailable" style={{ marginBottom: 12 }}>
          {t('cases.unavailable')}
        </div>
      )}
      <SubTabs
        label={t('cases.title')}
        active={tab}
        onSelect={select}
        tabs={[
          { id: 'cases', label: t('cases.tab.cases') },
          { id: 'decisions', label: t('cases.tab.decisions') },
          { id: 'precision', label: t('cases.tab.precision') },
        ]}
      />
      {tab === 'cases' && <CasesTab />}
      {tab === 'decisions' && (
        <DecisionsTab
          card={card}
          onSearch={(c) => {
            const p = new URLSearchParams(params);
            p.set('tab', 'decisions');
            if (c) p.set('card', c);
            else p.delete('card');
            setParams(p);
          }}
        />
      )}
      {tab === 'precision' && <PrecisionTab />}
    </>
  );
}

// ---- the case list ---------------------------------------------------------------------------

/** An over-long filter, said in the reader's language (QA-I-05) — the BFF's 400 is English and technical. */
const tooLongText = (keys: readonly CaseTextFilter[]) =>
  keys.map((k) => t('cases.filter.too_long', { field: t(`cases.filter.${k}`), n: num(CASE_FILTER_MAX[k]) })).join(' ');

/** A failed list read: a 400 is the filters (said plainly), anything else as the server put it. */
const listErrorText = (e: unknown) => (e instanceof ApiError && e.status === 400 ? t('cases.filter.rejected') : errorText(e));

function CasesTab() {
  const { settings, showToast } = useShell();
  const navigate = useNavigate();
  const from = useFrom();
  const [filters, setFilters] = useState<CaseFilters>({});
  /** What the text inputs hold before Apply. */
  const [draft, setDraft] = useState<CaseFilters>({});
  const [items, setItems] = useState<Case[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const query = useRef(0);

  const load = useCallback(
    async (f: CaseFilters) => {
      const mine = ++query.current;
      setItems(null);
      setError(null);
      try {
        const page = await api.cases.list(settings, f);
        if (mine !== query.current) return;
        setItems(page.items);
        setCursor(page.nextCursor);
        setError(null);
      } catch (e) {
        if (mine !== query.current) return;
        setItems([]);
        setCursor(undefined);
        setError(listErrorText(e));
      }
    },
    [settings],
  );

  useEffect(() => {
    void load(filters);
  }, [load, filters]);

  const more = async () => {
    if (!cursor) return;
    const mine = query.current;
    try {
      const page = await api.cases.list(settings, filters, cursor);
      if (mine !== query.current) return;
      setItems((prev) => [...(prev ?? []), ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) {
      // The rows already shown stay; only the next page failed.
      showToast(listErrorText(e), 'error');
    }
  };

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const next = { ...draft, ...(filters.status ? { status: filters.status } : {}) };
    const long = overlongFilters(next);
    if (long.length > 0) {
      // Not sent: the BFF would refuse it. Nothing was searched, so no "no cases match" either.
      query.current++;
      setItems([]);
      setCursor(undefined);
      setError(tooLongText(long));
      return;
    }
    setFilters(next);
  };

  /** The list could not be read (or the filters were refused): the message stands alone, no "0 shown", no empty row. */
  const failed = error !== null;

  return (
    <section className="card" data-testid="cases-list">
      <header className="card-head">
        <h2>{t('cases.list.title')}</h2>
        {items && !failed && (
          <span className="chip mute" data-testid="cases-count">
            {t('cases.list.count', { n: num(items.length) })}
          </span>
        )}
        <Tip>{t('cases.no_delete.tip')}</Tip>
      </header>

      <div className="row-wrap" role="group" aria-label={t('cases.filter.status.a11y')} style={{ marginBottom: 10 }}>
        <button
          type="button"
          className={`chip${filters.status ? '' : ' info'}`}
          aria-pressed={!filters.status}
          data-testid="cases-status-all"
          onClick={() => setFilters(({ status: _s, ...rest }) => rest)}
        >
          {t('cases.filter.all')}
        </button>
        {CASE_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`chip ${filters.status === s ? STATUS_CLASS[s] : ''}`}
            aria-pressed={filters.status === s}
            data-testid={`cases-status-${s}`}
            onClick={() => setFilters((f) => ({ ...f, status: s }))}
          >
            {statusLabel(s)}
          </button>
        ))}
        {/* QA-I-04 / QA-I-13: what the status chips and the Outcome filter each find, and how text matches. */}
        <Tip label={t('cases.filter.hint.a11y')}>
          <span data-testid="cases-filter-hint">
            {t('cases.filter.hint.outcome')} {t('cases.filter.hint.match')}
          </span>
        </Tip>
      </div>

      <form className="form-grid" onSubmit={apply} data-testid="cases-filters" style={{ marginBottom: 10 }}>
        <label className="field">
          {t('cases.filter.resolution')}
          <select
            value={draft.resolution ?? ''}
            onChange={(e) => {
              const resolution = (e.target.value || undefined) as CaseLabel | undefined;
              setDraft(({ resolution: _r, ...rest }) => (resolution ? { ...rest, resolution } : rest));
            }}
          >
            <option value="">{t('cases.filter.all')}</option>
            {CASE_LABELS.map((l) => (
              <option key={l} value={l}>
                {statusLabel(l)}
              </option>
            ))}
          </select>
        </label>
        {CASE_TEXT_FILTERS.map((k) => (
          <label key={k} className="field">
            {t(`cases.filter.${k}`)}
            <input
              type="text"
              className="mono-input"
              data-testid={`cases-filter-${k}`}
              value={draft[k] ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
            />
          </label>
        ))}
        <div className="row-wrap" style={{ alignSelf: 'end' }}>
          <button type="submit" className="btn small primary" data-testid="cases-apply">
            {t('cases.filter.apply')}
          </button>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              setDraft({});
              setFilters({});
            }}
          >
            {t('cases.filter.clear')}
          </button>
        </div>
      </form>

      {error && (
        <div className="banner error" role="status" data-testid="cases-error" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}

      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('cases.col.case')}</th>
              <th>{t('cases.col.status')}</th>
              <th>{t('col.decision.label')}</th>
              <th>{t('cases.col.card')}</th>
              <th>{t('cases.col.merchant')}</th>
              <th className="right">{t('decisions.col.amount')}</th>
              <th>{t('cases.col.rules')}</th>
              <th>{t('cases.col.assignee')}</th>
              <th>{t('cases.col.priority')}</th>
              <th>{t('cases.col.opened')}</th>
            </tr>
          </thead>
          <tbody>
            {items === null && !failed && (
              <tr>
                <td colSpan={10} className="empty">
                  {t('btn.loading.label')}
                </td>
              </tr>
            )}
            {items?.length === 0 && !failed && (
              <tr>
                <td colSpan={10} className="empty" role="status" data-testid="cases-empty">
                  {t('cases.list.empty')}
                </td>
              </tr>
            )}
            {items?.map((c) => (
              <tr key={c.id} className="clickable" data-testid={`case-row-${c.id}`} onClick={() => navigate(caseLink(c.cardId, c.id), { state: from })}>
                <td className="mono small nowrap">
                  {/* The row opens on a click anywhere; the id is its keyboard way in (UX review P1-5). */}
                  <Link className="rowlink" to={caseLink(c.cardId, c.id)} state={from} data-testid={`case-open-${c.id}`} onClick={(e) => e.stopPropagation()}>
                    {c.id}
                  </Link>
                  {isDemoCase(c.id) && (
                    <span className="chip warn" style={{ marginLeft: 6 }}>
                      {t('master.demo.chip')}
                    </span>
                  )}
                </td>
                <td>
                  <StatusChip status={c.status} resolution={c.resolution} />
                </td>
                <td>
                  <DecisionChip decision={c.decision} />
                </td>
                <td className="mono small">
                  <CardLink cardId={c.cardId} />
                </td>
                <td className="small">{c.txn.merchantName ?? c.merchantId ?? '—'}</td>
                {/* QA-I-03: ids and amounts never break mid-value; the text columns wrap instead. */}
                <td className="right mono nowrap">{num(c.txn.amountThb, 2)}</td>
                <td className="case-ids">
                  <RuleIds rules={c.firedRules} />
                </td>
                <td className="small">{c.assignee ?? '—'}</td>
                <td className="small">{c.priority ? t(`cases.priority.${c.priority}.label`) : '—'}</td>
                <td className="small nowrap">
                  <When iso={c.createdUtc} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cursor && !failed && (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn small" data-testid="cases-more" onClick={() => void more()}>
            {t('master.items.more')}
          </button>
        </div>
      )}
    </section>
  );
}

// ---- a card's decisions ------------------------------------------------------------------------

function DecisionsTab({ card, onSearch }: { card: string; onSearch: (card: string) => void }) {
  const { settings, showToast } = useShell();
  const navigate = useNavigate();
  const from = useFrom();
  const mayWrite = useMayWrite();
  const [typed, setTyped] = useState(card);
  const [items, setItems] = useState<InvestigateDecision[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [capped, setCapped] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<InvestigateDecision | null>(null);
  const query = useRef(0);

  const load = useCallback(async () => {
    const mine = ++query.current;
    setItems(null);
    setError(null);
    setCursor(undefined);
    setCapped(null);
    if (!card) return;
    // QA-I-05: a card number longer than the BFF takes is said plainly, and not searched.
    if (card.length > CASE_FILTER_MAX.cardId) {
      setError(t('cases.timeline.too_long', { n: num(CASE_FILTER_MAX.cardId) }));
      return;
    }
    try {
      const page = await api.cases.decisions(settings, card);
      if (mine !== query.current) return;
      setItems(page.items);
      setCursor(page.nextCursor);
      setCapped(page.capped ? (page.scanLimit ?? 0) : null);
    } catch (e) {
      if (mine !== query.current) return;
      setError(e instanceof ApiError && e.status === 400 ? t('cases.timeline.rejected') : errorText(e));
    }
  }, [settings, card]);

  useEffect(() => {
    setTyped(card);
    void load();
  }, [load, card]);

  const more = async () => {
    if (!cursor) return;
    const mine = query.current;
    try {
      const page = await api.cases.decisions(settings, card, cursor);
      if (mine !== query.current) return;
      setItems((prev) => [...(prev ?? []), ...page.items]);
      setCursor(page.nextCursor);
    } catch (e) {
      // The rows already shown stay; only the next page failed.
      showToast(errorText(e), 'error');
    }
  };

  return (
    <section className="card" data-testid="cases-timeline">
      <header className="card-head">
        <h2>{t('cases.timeline.title')}</h2>
        <Tip>{t('cases.timeline.tip')}</Tip>
      </header>
      <form
        className="row-wrap"
        style={{ marginBottom: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          onSearch(typed.trim());
        }}
      >
        <input
          type="search"
          className="mono-input"
          style={{ width: 260 }}
          aria-label={t('cases.timeline.card.a11y')}
          placeholder={t('cases.timeline.card.placeholder')}
          data-testid="cases-card-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <button type="submit" className="btn small primary" data-testid="cases-card-search" disabled={typed.trim() === ''}>
          {t('cases.timeline.search')}
        </button>
      </form>

      {capped !== null && (
        <div className="banner warn" role="status" data-testid="cases-timeline-capped" style={{ marginBottom: 10 }}>
          {t('cases.timeline.capped', { n: num(capped) })}
        </div>
      )}
      {error && (
        <div className="banner error" role="status" data-testid="cases-timeline-error" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}

      {card && !error && (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('col.time.label')}</th>
                <th>{t('col.decision.label')}</th>
                <th>{t('cases.col.rules')}</th>
                <th className="right">{t('decisions.col.amount')}</th>
                <th>{t('cases.col.merchant')}</th>
                <th>{t('cases.col.case')}</th>
              </tr>
            </thead>
            <tbody>
              {items === null && (
                <tr>
                  <td colSpan={6} className="empty">
                    {t('btn.loading.label')}
                  </td>
                </tr>
              )}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty" role="status" data-testid="cases-timeline-empty">
                    {t('cases.timeline.empty')}
                  </td>
                </tr>
              )}
              {items?.map((d) => (
                <tr key={d.decisionId} data-testid={`timeline-${d.decisionId}`}>
                  <td className="small nowrap">
                    <When iso={d.decisionUtc} />
                  </td>
                  <td>
                    <DecisionChip decision={d.decision} />
                  </td>
                  <td className="case-ids">
                    <RuleIds rules={d.firedRules} />
                  </td>
                  <td className="right mono nowrap">{num(d.txn.amountThb, 2)}</td>
                  <td className="small">{d.txn.merchantName ?? d.merchantId ?? '—'}</td>
                  <td className="nowrap">
                    {d.caseId ? (
                      <Link to={caseLink(d.cardId, d.caseId)} state={from} data-testid={`timeline-view-${d.decisionId}`}>
                        {t('cases.view.action')}
                      </Link>
                    ) : mayWrite() && !DEMO ? (
                      <button type="button" className="btn small" data-testid={`timeline-open-${d.decisionId}`} onClick={() => setOpening(d)}>
                        {t('cases.open.action')}
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cursor && !error && (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn small" data-testid="cases-timeline-more" onClick={() => void more()}>
            {t('master.items.more')}
          </button>
        </div>
      )}

      {opening && (
        <OpenCaseDialog
          decision={opening}
          onClose={() => setOpening(null)}
          onOpened={(c) => {
            setOpening(null);
            showToast(t('cases.opened.toast', { id: c.id }));
            navigate(caseLink(c.cardId, c.id), { state: from });
          }}
          onExists={(message) => {
            setOpening(null);
            showToast(message, 'error');
            void load();
          }}
        />
      )}
    </section>
  );
}

function Problem({ text, testid }: { text: string | null; testid: string }) {
  if (!text) return null;
  return (
    <div className="why-missing" role="alert" data-testid={testid} style={{ margin: 0 }}>
      {text}
    </div>
  );
}

function OpenCaseDialog({
  decision,
  onClose,
  onOpened,
  onExists,
}: {
  decision: InvestigateDecision;
  onClose: () => void;
  onOpened: (c: Case) => void;
  onExists: (message: string) => void;
}) {
  const { settings } = useShell();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      onOpened(await api.cases.open(settings, decision.cardId, decision.decisionId, note.trim() || undefined));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'case_exists') onExists(e.message);
      else setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      busy={busy}
      title={t('cases.open.title')}
      testid="case-open-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button type="button" className="btn primary" data-testid="case-open-confirm" disabled={busy} onClick={() => void open()}>
            {t('cases.open.action')}
          </button>
        </>
      }
    >
      <div className="small">
        <DecisionChip decision={decision.decision} /> {dateTime(decision.decisionUtc)} · <RuleIds rules={decision.firedRules} plain />
      </div>
      <label className="field">
        {t('cases.note.optional')}
        <textarea data-testid="case-open-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Problem testid="case-open-error" text={error} />
    </Dialog>
  );
}

// ---- rule precision ----------------------------------------------------------------------------

const PRECISION_COLUMNS: { key: PrecisionSortKey; label: string; numeric: boolean }[] = [
  { key: 'ruleId', label: 'col.rule.label', numeric: false },
  { key: 'precision', label: 'cases.precision.col.precision', numeric: true },
  { key: 'confirmedFraud', label: 'cases.status.ConfirmedFraud.label', numeric: true },
  { key: 'falsePositive', label: 'cases.status.FalsePositive.label', numeric: true },
  { key: 'genuine', label: 'cases.status.Genuine.label', numeric: true },
  { key: 'open', label: 'cases.precision.col.open', numeric: true },
];

function PrecisionTab() {
  const { settings } = useShell();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState<RulePrecisionReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: PrecisionSortKey; dir: 'asc' | 'desc' }>({ key: 'precision', dir: 'desc' });

  useEffect(() => {
    let live = true;
    setReport(null);
    // The date inputs are inclusive Thai days; the API takes instants, `to` exclusive (QA-I-01).
    api.cases
      .precision(settings, bangkokDayRange(from, to))
      .then(
        (r) => {
          if (!live) return;
          setReport(r);
          setError(null);
        },
        (e: unknown) => {
          if (!live) return;
          setReport({ cases: 0, rules: [] });
          setError(errorText(e));
        },
      );
    return () => {
      live = false;
    };
  }, [settings, from, to]);

  const rows = useMemo(() => (report ? sortPrecision(report.rules, sort.key, sort.dir) : []), [report, sort]);

  const toggle = (key: PrecisionSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'ruleId' ? 'asc' : 'desc' }));

  return (
    <section className="card" data-testid="cases-precision">
      <header className="card-head">
        <h2>{t('cases.precision.title')}</h2>
        {report && (
          <span className="chip mute" data-testid="cases-precision-count">
            {t('cases.precision.cases', { n: num(report.cases) })}
          </span>
        )}
        <Tip>{t('cases.precision.tip')}</Tip>
      </header>
      <div className="form-grid" style={{ marginBottom: 10 }}>
        <label className="field">
          {t('cases.precision.from')}
          {/* "Today" is the Thai date, whatever the browser's own zone. */}
          <input type="date" aria-label={t('cases.precision.from')} value={from} max={to || thaiDay(new Date())} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          {t('cases.precision.to')}
          <input type="date" aria-label={t('cases.precision.to')} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      {error && (
        <div className="banner error" role="status" data-testid="cases-precision-error" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}
      <div className="tbl-wrap">
        <table data-testid="cases-precision-table">
          <thead>
            <tr>
              {PRECISION_COLUMNS.slice(0, 1).map((c) => (
                <SortHead key={c.key} col={c} sort={sort} onSort={toggle} />
              ))}
              <th>{t('col.rule_name.label')}</th>
              {PRECISION_COLUMNS.slice(1).map((c) => (
                <SortHead key={c.key} col={c} sort={sort} onSort={toggle} />
              ))}
            </tr>
          </thead>
          <tbody>
            {report === null && (
              <tr>
                <td colSpan={7} className="empty">
                  {t('btn.loading.label')}
                </td>
              </tr>
            )}
            {report?.rules.length === 0 && (
              <tr>
                <td colSpan={7} className="empty" role="status" data-testid="cases-precision-empty">
                  {t('cases.precision.empty')}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.ruleId} data-testid={`precision-${r.ruleId}`}>
                <td className="mono nowrap">{r.ruleId}</td>
                <td className="small">{r.name ?? '—'}</td>
                <td className="right mono" data-testid={`precision-value-${r.ruleId}`}>
                  {precisionText(r.precision)}{' '}
                  <span className="muted small">{t('cases.precision.of', { n: num(r.confirmedFraud), total: num(r.labelled) })}</span>
                </td>
                <td className="right mono">{num(r.confirmedFraud)}</td>
                <td className="right mono">{num(r.falsePositive)}</td>
                <td className="right mono">{num(r.genuine)}</td>
                <td className="right mono">{num(r.open)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortHead({
  col,
  sort,
  onSort,
}: {
  col: (typeof PRECISION_COLUMNS)[number];
  sort: { key: PrecisionSortKey; dir: 'asc' | 'desc' };
  onSort: (k: PrecisionSortKey) => void;
}) {
  const active = sort.key === col.key;
  return (
    <th className={col.numeric ? 'right' : undefined} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="btn link" data-testid={`precision-sort-${col.key}`} onClick={() => onSort(col.key)}>
        {t(col.label)}
        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
}

// ---- one case -----------------------------------------------------------------------------------

/** The button that moves a case to `to` — worded for where it comes from. */
function moveLabel(from: CaseStatus, to: CaseStatus): string {
  if (to === 'UnderReview') return t(from === 'Open' ? 'cases.move.review.label' : from === 'Closed' ? 'cases.move.reopen.label' : 'cases.move.relabel.label');
  return t(`cases.move.${to}.label`);
}

function CaseDetail({ cardId, id }: { cardId: string; id: string }) {
  const { settings, session, showToast } = useShell();
  const mayWrite = useMayWrite()(id);
  const primaryId = usePrimarySetId();
  // "← back" returns to where the case was opened from — the card's timeline, a filtered tab (QA-I-07).
  const came = (useLocation().state as FromState)?.from;
  const backTo = came && /^\/investigate(\?|$)/.test(came) ? came : '/investigate';
  const [kase, setKase] = useState<Case | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<CaseStatus | null>(null);

  const load = useCallback(async () => {
    try {
      setKase(await api.cases.get(settings, cardId, id));
      setMissing(false);
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true);
      else setError(errorText(e));
    }
  }, [settings, cardId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** A write answered 409: somebody changed the case meanwhile. Show theirs, say so. */
  const onWriteError = (e: unknown) => {
    showToast(e instanceof ApiError && e.code === 'etag_mismatch' ? t('cases.stale.toast') : errorText(e), 'error');
    if (e instanceof ApiError && e.status === 409) void load();
  };

  const back = (
    <Link to={backTo} className="small" data-testid="case-back">
      {t('cases.back')}
    </Link>
  );

  if (missing) {
    return (
      <>
        {back}
        <PageHead title={t('cases.title')} />
        <div className="empty" role="status" data-testid="case-notfound">
          {t('cases.notfound')}
        </div>
      </>
    );
  }

  return (
    <>
      {back}
      <PageHead
        title={id}
        chips={
          <>
            {kase && (
              <span style={{ marginLeft: 8 }}>
                <StatusChip status={kase.status} resolution={kase.resolution} />
              </span>
            )}
            {isDemoCase(id) && <span className="chip warn">{t('master.demo.chip')}</span>}
          </>
        }
        lead={t('cases.no_delete.tip')}
      />
      {error && (
        <div className="banner error" role="status" data-testid="case-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}
      {/* UX review P2-12: a skeleton of the page while the case loads, not the previous screen or a blank. */}
      {!kase && !error && <CaseSkeleton />}
      {kase && (
        /* UX review P2-12: two columns from 1280 px — what happened on the left (the transaction,
           the rules that fired), the analyst's work on the right, kept in view while the left scrolls. */
        <div className="case-grid" data-testid="case-grid">
          <div className="case-main">
            <SnapshotPanel kase={kase} />

            <section className="card" data-testid="case-rules">
              <header className="card-head">
                <h2>{t('cases.rules.title')}</h2>
              </header>
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('col.rule.label')}</th>
                      <th>{t('col.rule_name.label')}</th>
                      <th>{t('col.action.label')}</th>
                      <th>{t('cases.rules.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kase.firedRules.length === 0 && (
                      <tr>
                        <td colSpan={4} className="empty">
                          {t('cases.rules.none')}
                        </td>
                      </tr>
                    )}
                    {kase.firedRules.map((r) => (
                      <tr key={r.ruleId} data-testid={`case-rule-${r.ruleId}`}>
                        {/* QA-I-08: the rule opens in Rule Management, in its own set when the hit names one. */}
                        <td className="mono nowrap">
                          {NOT_A_RULE.has(r.ruleId) ? (
                            r.ruleId
                          ) : (
                            <Link to={ruleLink(r.ruleId, r.ruleSetId, primaryId)} title={t('links.rule.title', { id: r.ruleSetId ? `${r.ruleSetId}/${r.ruleId}` : r.ruleId })}>
                              {r.ruleId}
                            </Link>
                          )}
                        </td>
                        <td>{r.name ?? '—'}</td>
                        <td>{r.action ? <ActionChip action={r.action as RuleAction} /> : '—'}</td>
                        <td className="small mono">
                          <HitDetail detail={r.detail} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {kase.stateIncomplete.length > 0 && (
                <div className="small muted" data-testid="case-state-incomplete" style={{ marginTop: 8 }}>
                  {t('cases.rules.state_incomplete', { rules: kase.stateIncomplete.join(', ') })}
                </div>
              )}
              {kase.skippedRules.length > 0 && (
                <div className="small muted" style={{ marginTop: 8 }}>
                  {t('cases.rules.skipped', { rules: kase.skippedRules.join(', ') })}
                </div>
              )}
            </section>
          </div>

          <aside className="case-side" aria-label={t('cases.workflow.title')}>
            <section className="card" data-testid="case-workflow">
              <header className="card-head">
                <h2>{t('cases.workflow.title')}</h2>
              </header>
              {mayWrite && kase.allowedTransitions.length > 0 && (
                <div className="row-wrap case-moves">
                  {kase.allowedTransitions.map((to) => (
                    <button
                      key={to}
                      type="button"
                      className={`btn small${to === 'ConfirmedFraud' ? ' danger' : ''}`}
                      data-testid={`case-move-${to}`}
                      onClick={() => setMoving(to)}
                    >
                      {moveLabel(kase.status, to)}
                    </button>
                  ))}
                </div>
              )}
              <Assignment kase={kase} mayWrite={mayWrite} onSaved={setKase} onError={onWriteError} />
            </section>

            <Notes kase={kase} mayWrite={mayWrite} actor={session?.user.actor} onSaved={setKase} onError={onWriteError} />

            <section className="card" data-testid="case-history">
              <header className="card-head">
                <h2>{t('cases.history.title')}</h2>
              </header>
              <ol className="histlist">
                {kase.history.map((h, i) => (
                  <li key={`${h.utc}-${i}`} data-testid={`case-history-${i}`}>
                    <div>{h.from ? `${statusLabel(h.from)} → ${statusLabel(h.to)}` : t('cases.history.opened')}</div>
                    <div className="small muted">
                      {/* QA-I-09: the person as "Opened by" shows them; the full audit form in the tooltip. */}
                      <When iso={h.utc} /> · <span title={h.actor}>{actorLabel(h.actor)}</span>
                    </div>
                    {h.note && <div className="small histnote">{h.note}</div>}
                  </li>
                ))}
              </ol>
            </section>
          </aside>
        </div>
      )}

      {moving && kase && (
        <TransitionDialog
          kase={kase}
          to={moving}
          onClose={() => setMoving(null)}
          onMoved={(c) => {
            setMoving(null);
            setKase(c);
            showToast(t('cases.moved.toast', { status: statusLabel(c.status) }));
          }}
          onError={(e) => {
            setMoving(null);
            onWriteError(e);
          }}
        />
      )}
    </>
  );
}

/** UX review P2-12: the case page's shape while it loads — head, the key/value block, the side column. */
function CaseSkeleton() {
  return (
    <div className="case-grid" data-testid="case-skeleton" aria-busy="true">
      <span className="visually-hidden" role="status">
        {t('cases.loading')}
      </span>
      <div className="case-main" aria-hidden="true">
        <section className="card">
          <div className="skel skel-head" />
          <div className="kv">
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className="kv-row">
                <span className="skel skel-k" />
                <span className="skel skel-v" />
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="case-side" aria-hidden="true">
        <section className="card">
          <div className="skel skel-head" />
          <div className="skel skel-block" />
        </section>
      </div>
    </div>
  );
}

const yesNo = (b: boolean | undefined) => (b === undefined ? '—' : t(b ? 'cases.flag.yes' : 'cases.flag.no'));

function SnapshotPanel({ kase }: { kase: Case }) {
  const x = kase.txn;
  return (
    <section className="card" data-testid="case-snapshot">
      <header className="card-head">
        <h2>{t('cases.snapshot.title')}</h2>
        <Tip>{t('cases.snapshot.tip')}</Tip>
      </header>
      <div className="kv">
        <Kv k={t('col.decision.label')} v={<DecisionChip decision={kase.decision} />} />
        <Kv k={t('col.time.label')} v={<When iso={kase.decisionUtc} />} clip testid="case-snap-time" />
        {/* UX review P2-13: short form like every list, full id in the tooltip and the link, a copy button. */}
        <Kv k={t('cases.col.card')} v={<CardLink cardId={kase.cardId} testid="case-snap-card" />} mono clip />
        <Kv k={t('cases.snap.customer')} v={kase.customerId ?? '—'} mono clip />
        <Kv k={t('cases.snap.txn')} v={x.transactionId ?? '—'} mono clip />
        <Kv k={t('cases.col.merchant')} v={[x.merchantName, x.merchantId].filter(Boolean).join(' · ') || '—'} testid="case-snap-merchant" />
        <Kv k={t('cases.snap.city')} v={x.merchantCity ?? '—'} />
        <Kv k={t('decisions.col.amount')} v={num(x.amountThb, 2)} mono testid="case-snap-amount" />
        <Kv k={t('cases.snap.amount_txn')} v={x.amount === undefined ? '—' : `${num(x.amount, 2)} · ${x.currencyCodeNum ?? '—'}`} mono />
        <Kv k={t('cases.snap.channel')} v={x.channelId ?? '—'} mono />
        <Kv k={t('cases.snap.txn_type')} v={x.txnType ?? '—'} mono />
        <Kv k="MCC" v={x.mcc ?? '—'} mono />
        <Kv k={t('cases.snap.country')} v={x.merchantCountryNum ?? '—'} mono />
        <Kv k={t('cases.snap.entry_mode')} v={x.entryMode ?? '—'} mono />
        <Kv k={t('cases.snap.card_present')} v={yesNo(x.cardPresent)} />
        <Kv k={t('cases.snap.ecommerce')} v={yesNo(x.ecommerce)} />
        <Kv k={t('cases.snap.network')} v={x.network ?? '—'} mono />
        <Kv k={t('cases.snap.terminal')} v={x.terminalId ?? '—'} mono clip />
        <Kv k={t('cases.snap.fraud_score')} v={x.fraudScore ?? '—'} mono />
        <Kv k={t('cases.snap.rule_set_version')} v={kase.ruleSetVersion ?? '—'} mono clip />
        <Kv k={t('cases.snap.latency')} v={`${ms(kase.engineLatencyMs)} · ${ms(kase.serviceLatencyMs)}`} mono />
        <Kv k={t('cases.snap.degraded')} v={yesNo(kase.degraded)} />
        {/* When and who on two rows: one line each, neither breaks mid-token. */}
        <Kv k={t('cases.col.opened')} v={<When iso={kase.createdUtc} />} clip testid="case-snap-opened" />
        <Kv k={t('cases.col.opened_by')} v={<span title={kase.createdBy}>{actorLabel(kase.createdBy)}</span>} clip testid="case-snap-opened-by" />
      </div>
    </section>
  );
}

function Assignment({
  kase,
  mayWrite,
  onSaved,
  onError,
}: {
  kase: Case;
  mayWrite: boolean;
  onSaved: (c: Case) => void;
  onError: (e: unknown) => void;
}) {
  const { settings, showToast } = useShell();
  const [assignee, setAssignee] = useState(kase.assignee ?? '');
  const [priority, setPriority] = useState<CasePriority | ''>(kase.priority ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setAssignee(kase.assignee ?? '');
    setPriority(kase.priority ?? '');
  }, [kase.assignee, kase.priority]);

  const changed = assignee.trim() !== (kase.assignee ?? '') || priority !== (kase.priority ?? '');
  const save = async () => {
    setBusy(true);
    try {
      onSaved(await api.cases.update(settings, kase, { assignee: assignee.trim() || null, priority: priority || null }));
      showToast(t('cases.assign.saved.toast'));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-grid" data-testid="case-assignment">
      <label className="field">
        {t('cases.col.assignee')}
        <input
          type="text"
          data-testid="case-assignee"
          disabled={!mayWrite}
          value={assignee}
          placeholder={t('cases.assign.placeholder')}
          onChange={(e) => setAssignee(e.target.value)}
        />
      </label>
      <label className="field">
        {t('cases.col.priority')}
        <select data-testid="case-priority" disabled={!mayWrite} value={priority} onChange={(e) => setPriority(e.target.value as CasePriority | '')}>
          <option value="">{t('cases.priority.none.label')}</option>
          {CASE_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {t(`cases.priority.${p}.label`)}
            </option>
          ))}
        </select>
      </label>
      {mayWrite && (
        <div style={{ alignSelf: 'end' }}>
          <button type="button" className="btn small primary" data-testid="case-assign-save" disabled={!changed || busy} onClick={() => void save()}>
            {t('btn.save.label')}
          </button>
        </div>
      )}
    </div>
  );
}

function TransitionDialog({
  kase,
  to,
  onClose,
  onMoved,
  onError,
}: {
  kase: Case;
  to: CaseStatus;
  onClose: () => void;
  onMoved: (c: Case) => void;
  onError: (e: unknown) => void;
}) {
  const { settings } = useShell();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Reopening a closed case, and closing one straight from Open (no outcome), must say why —
  // the admin service refuses either without a note.
  const closingWithoutOutcome = kase.status === 'Open' && to === 'Closed';
  const noteRequired = kase.status === 'Closed' || closingWithoutOutcome;
  const move = async () => {
    setBusy(true);
    setProblem(null);
    try {
      onMoved(await api.cases.transition(settings, kase, to, note.trim() || undefined));
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        setProblem(errorText(e));
        setBusy(false);
      } else onError(e);
    }
  };
  return (
    <Dialog
      busy={busy}
      title={t('cases.move.title', { from: statusLabel(kase.status), to: statusLabel(to) })}
      testid="case-move-dialog"
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="case-move-confirm"
            disabled={busy || (noteRequired && note.trim() === '')}
            onClick={() => void move()}
          >
            {moveLabel(kase.status, to)}
          </button>
        </>
      }
    >
      <label className="field">
        {closingWithoutOutcome ? t('cases.note.close_required') : noteRequired ? t('cases.note.required') : t('cases.note.optional')}
        <textarea data-testid="case-move-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Problem testid="case-move-error" text={problem} />
    </Dialog>
  );
}

function Notes({
  kase,
  mayWrite,
  actor,
  onSaved,
  onError,
}: {
  kase: Case;
  mayWrite: boolean;
  actor: string | undefined;
  onSaved: (c: Case) => void;
  onError: (e: unknown) => void;
}) {
  const { settings } = useShell();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  /** QA-I-10: the note waiting for its delete to be confirmed. */
  const [deleting, setDeleting] = useState<CaseNote | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (call: () => Promise<Case>) => {
    setBusy(true);
    try {
      onSaved(await call());
      return true;
    } catch (e) {
      onError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" data-testid="case-notes">
      <header className="card-head">
        <h2>{t('cases.notes.title')}</h2>
        <span className="chip mute">{num(kase.notes.length)}</span>
        <Tip>{t('cases.notes.tip', { n: kase.noteEditMinutes })}</Tip>
      </header>
      {kase.notes.length === 0 && (
        <div className="empty" data-testid="case-notes-empty">
          {t('cases.notes.empty')}
        </div>
      )}
      <div className="stack">
        {kase.notes.map((n) => {
          const own = mayWrite && noteEditable(n, actor, kase.noteEditMinutes);
          return (
            <div key={n.id} className="kv-row note-row" data-testid={`case-note-${n.id}`}>
              <span className="k small">
                <When iso={n.utc} />
                <br />
                {/* QA-I-09: the person as "Opened by" shows them; the full audit form in the tooltip. */}
                <span title={n.author}>{actorLabel(n.author)}</span>
                {n.editedUtc && <span className="muted"> · {t('cases.notes.edited')}</span>}
              </span>
              {editing?.id === n.id ? (
                <textarea data-testid="case-note-edit-text" value={editing.text} onChange={(e) => setEditing({ id: n.id, text: e.target.value })} />
              ) : (
                <span className="v" style={{ whiteSpace: 'pre-wrap' }}>
                  {n.text}
                </span>
              )}
              <span className="row-wrap">
                {own && editing?.id !== n.id && (
                  <>
                    <button type="button" className="btn small" data-testid={`case-note-edit-${n.id}`} onClick={() => setEditing({ id: n.id, text: n.text })}>
                      {t('btn.edit.label')}
                    </button>
                    <button
                      type="button"
                      className="btn small danger"
                      data-testid={`case-note-delete-${n.id}`}
                      disabled={busy}
                      onClick={() => setDeleting(n)}
                    >
                      {t('master.menu.delete')}
                    </button>
                  </>
                )}
                {editing?.id === n.id && (
                  <>
                    <button
                      type="button"
                      className="btn small primary"
                      data-testid="case-note-edit-save"
                      disabled={busy || editing.text.trim() === ''}
                      onClick={() =>
                        void run(() => api.cases.editNote(settings, kase, n.id, editing.text.trim())).then((ok) => ok && setEditing(null))
                      }
                    >
                      {t('btn.save.label')}
                    </button>
                    <button type="button" className="btn small" onClick={() => setEditing(null)}>
                      {t('btn.cancel.label')}
                    </button>
                  </>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {mayWrite && (
        <form
          className="row-wrap"
          style={{ marginTop: 10, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.cases.addNote(settings, kase, text.trim())).then((ok) => ok && setText(''));
          }}
        >
          <textarea
            style={{ flex: 1 }}
            aria-label={t('cases.notes.add.a11y')}
            placeholder={t('cases.notes.add.placeholder')}
            data-testid="case-note-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="btn small primary" data-testid="case-note-add" disabled={busy || text.trim() === ''}>
            {t('cases.notes.add.action')}
          </button>
        </form>
      )}
      {deleting && (
        <Dialog
          busy={busy}
          title={t('cases.notes.delete.title')}
          testid="case-note-delete-dialog"
          onClose={() => setDeleting(null)}
          foot={
            <>
              <span className="spacer" />
              <button type="button" className="btn" onClick={() => setDeleting(null)}>
                {t('btn.cancel.label')}
              </button>
              <button
                type="button"
                className="btn danger"
                data-testid="case-note-delete-confirm"
                disabled={busy}
                onClick={() => void run(() => api.cases.deleteNote(settings, kase, deleting.id)).then(() => setDeleting(null))}
              >
                {t('master.menu.delete')}
              </button>
            </>
          }
        >
          <div>{t('cases.notes.delete.confirm')}</div>
          <blockquote className="small note-quote">{deleting.text}</blockquote>
        </Dialog>
      )}
    </section>
  );
}
